import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useMatch, useNavigate } from 'react-router-dom'

import { Banner } from '~/components/Banner'
import { IconButton } from '~/components/IconButton'
import { PageHeader } from '~/components/PageHeader'
import Select from '~/components/Select'
import Spinner from '~/components/Spinner'
import Tile from '~/components/Tile'
import { VeSIS } from '~/components/VeSIS'
import { useVESISParams } from '~/hooks/useVESISParams'
import { CONFIG, getChainById } from '~/lib/config'
import { isLockDateExpired } from '~/lib/isLockDateExpired'
import { useLockDetails } from '~/lib/useLockDetails'
import { useWallet, WalletStatus } from '~/lib/useWallet'

import { AboutLock } from './AboutLock'
import SettingsIcon from './assets/settings.svg'
import { NoActiveLock } from './NoActiveLock'
import { TotalStakingOverview } from './TotalStakingOverview'

import styles from './VESISPage.pcss'
import Button from '~/components/Button'
import { useRewardsV2 } from '~/lib/useRewardsV2'
import { VESIS_SEO_DATA } from './constants'

const OPTIONS = CONFIG.vesis.map((vesisConfig) => {
	const chain = getChainById(vesisConfig.chain.id)

	return {
		label: chain.name,
		value: vesisConfig.slug,
		chainId: chain.id,
	}
})

export function VESISPage() {
	const navigate = useNavigate()
	const { config } = useVESISParams()
	const wallet = useWallet()

	const match = useMatch('/vesis/:network/*')
	const networkSlug = match?.params.network || 'mainnet'
	const rewards = useRewardsV2()
	const seoData = VESIS_SEO_DATA[networkSlug] || VESIS_SEO_DATA.mainnet

	useEffect(() => {
		if (
			wallet.status === WalletStatus.CONNECTED &&
			!wallet.isCurrentChain(config.chain.id)
		) {
			wallet.setChain(config.chain.id).catch(() => {})
		}
	}, [config.chain.id, wallet.status])

	const [isAboutLockDisabled, setIsAboutLockDisabled] = useState(false)

	const { data, isLoading } = useLockDetails(() => {
		if (!isAboutLockDisabled) {
			return
		}

		setIsAboutLockDisabled(false)
	})

	let rightLink: React.ReactNode
	if (data && !isLockDateExpired(data.end.toNumber())) {
		rightLink = (
			<IconButton className={styles.settings} asChild>
				<Link to={`/vesis/${config.slug}/settings`}>
					<SettingsIcon />
				</Link>
			</IconButton>
		)
	}

	let lockContent: React.ReactNode
	if (isLoading) {
		lockContent = <Spinner className={styles.spinner} />
	} else if (data) {
		const onClaimSuccess = () => {
			setIsAboutLockDisabled(true)
		}

		lockContent = (
			<AboutLock
				disabled={isAboutLockDisabled}
				lock={data}
				onClaimSuccess={onClaimSuccess}
			/>
		)
	} else {
		lockContent = <NoActiveLock />
	}

	return (
		<div className={styles.page}>
			<Helmet>
				<title>{seoData.title}</title>
				<meta name="description" content={seoData.description} />
				<link
					rel="canonical"
					href={`https://rewards.symbiosis.finance/vesis/${networkSlug}`}
				/>
			</Helmet>

			<Tile>
				<div className={styles.container}>
					<PageHeader
						title="Lock your SIS and get veSIS rewards"
						onQuestionClick={() => {
							window.open(
								'https://docs.symbiosis.finance/liquidity-providers/vesis',
								'_blank'
							)
							// TODO show bubbles
						}}
						rightLink={
							<div className={styles.rightLinks}>
								{rightLink}
								<Select
									options={OPTIONS}
									value={networkSlug}
									onChange={({ value }) =>
										navigate(`/vesis/${value}`)
									}
								/>
							</div>
						}
					/>

					{lockContent}

					<VeSIS threshold={rewards?.vesisThreshold} />

					<Banner
						title="Great news for all veSIS holders!"
						hint="https://x.com/symbiosis_fi/status/1851501047455629702"
					>
						<div>
							$SCR and $TAIKO airdrop is available to claim.
						</div>
						<div>
							This is one of the exclusive benefits for veSIS
							holders.
						</div>
						<div>
							<br />
							<Link to="/claim-rewards">
								<Button className={styles.goToRewardsButton}>
									Go to Claim
								</Button>
							</Link>
						</div>
					</Banner>

					<TotalStakingOverview />
				</div>
			</Tile>
		</div>
	)
}
